import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Download from 'yet-another-react-lightbox/plugins/download';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useState, useEffect } from 'react';
import { TransactionUpload } from '~/types/fundrequests';
import { isPdfFile } from '~/components/FundRequests/utils/fileType';
import { MdClose, MdDownload, MdZoomIn, MdZoomOut, MdCenterFocusWeak, MdRotateLeft, MdRotateRight } from 'react-icons/md';

// Set up the worker for react-pdf - use stable CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FundrequestLightboxProps {
    isOpen: boolean;
    onClose: () => void;
    uploads: TransactionUpload[];
    slideIndex: number | null;
}

const PDFViewer = ({ url, onClose, filename }: { url: string; onClose: () => void; filename?: string }) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);

    // Zoom + rotation controls. Some uploads (e.g. phone photos converted to PDF
    // by Acrobat) carry a /Rotate flag that pdf.js applies but the original
    // viewer ignores, so the page renders rotated and the admin needs to correct it.
    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [baseRotation, setBaseRotation] = useState<number>(0);
    const [userRotation, setUserRotation] = useState<number>(0);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
        setLoading(false);
    }

    function onPageLoadSuccess(page: { rotate?: number }) {
        setBaseRotation(page?.rotate ?? 0);
    }

    const zoomIn = () => setZoomLevel(prev => Math.min(prev * 1.5, 5));
    const zoomOut = () => setZoomLevel(prev => Math.max(prev / 1.5, 0.5));
    const resetView = () => {
        setZoomLevel(1);
        setUserRotation(0);
    };
    const rotateLeft = () => setUserRotation(prev => (prev + 270) % 360);
    const rotateRight = () => setUserRotation(prev => (prev + 90) % 360);

    function onDocumentLoadError(error: Error) {
        console.error('Error loading PDF:', error);
        setLoading(false);
    }

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'document.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(numPages, prev + 1));
    };

    // Handle click outside to close
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    goToPrevPage();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    goToNextPage();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [numPages, pageNumber]);

    return (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75"
            onClick={handleBackdropClick}
        >
            <div className="relative max-h-[95vh] max-w-[95vw] bg-white rounded-lg shadow-2xl overflow-hidden">
                {/* Header with controls */}
                <div className="sticky top-0 z-[100000] bg-white border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {filename || 'PDF Document'}
                            </h3>
                            {numPages > 0 && (
                                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                    Page {pageNumber} of {numPages}
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Zoom + rotate controls */}
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={rotateLeft}
                                    className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                                    title="Rotate left"
                                >
                                    <MdRotateLeft size={18} />
                                </button>
                                <button
                                    onClick={rotateRight}
                                    className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                                    title="Rotate right"
                                >
                                    <MdRotateRight size={18} />
                                </button>
                                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                                <button
                                    onClick={zoomOut}
                                    disabled={zoomLevel <= 0.5}
                                    className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Zoom out"
                                >
                                    <MdZoomOut size={18} />
                                </button>
                                <span className="text-xs text-gray-500 min-w-[35px] text-center">
                                    {Math.round(zoomLevel * 100)}%
                                </span>
                                <button
                                    onClick={zoomIn}
                                    disabled={zoomLevel >= 5}
                                    className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Zoom in"
                                >
                                    <MdZoomIn size={18} />
                                </button>
                                <button
                                    onClick={resetView}
                                    disabled={zoomLevel === 1 && userRotation === 0}
                                    className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Reset view"
                                >
                                    <MdCenterFocusWeak size={18} />
                                </button>
                                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                            </div>
                            {/* Download link */}
                            <a
                                href={url}
                                download={filename || 'document.pdf'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors font-medium"
                                style={{
                                    color: '#2563EB',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    zIndex: 100001
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#1D4ED8';
                                    e.currentTarget.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#2563EB';
                                    e.currentTarget.style.textDecoration = 'none';
                                }}
                                title="Download PDF"
                            >
                                <MdDownload size={16} />
                                Download PDF
                            </a>

                            <button
                                onClick={onClose}
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                                title="Close PDF viewer (ESC)"
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Navigation controls */}
                    {numPages > 1 && (
                        <div className="flex justify-center gap-3 mt-4">
                            <button
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title="Previous page (←)"
                            >
                                ← Previous
                            </button>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Page</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={numPages}
                                    value={pageNumber}
                                    onChange={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= numPages) {
                                            setPageNumber(page);
                                        }
                                    }}
                                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-600">of {numPages}</span>
                            </div>

                            <button
                                onClick={goToNextPage}
                                disabled={pageNumber >= numPages}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title="Next page (→)"
                            >
                                Next →
                            </button>
                        </div>
                    )}

                    {/* Keyboard shortcuts hint */}
                    <div className="text-xs text-gray-500 text-center mt-2">
                        Use ← → arrow keys to navigate • ESC to close • Click outside to close
                    </div>
                </div>

                {/* PDF Content */}
                <div className="overflow-auto max-h-[calc(95vh-140px)] bg-gray-50 flex justify-center p-4">
                    {loading && (
                        <div className="text-center p-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading PDF...</p>
                        </div>
                    )}

                    <Document
                        file={url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                        error={
                            <div className="text-center p-8">
                                <p className="text-red-500 mb-2 text-lg">Failed to load PDF</p>
                                <p className="text-gray-600 text-sm">Please check if the file is a valid PDF document.</p>
                                <button
                                    onClick={handleDownload}
                                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                    Try downloading instead
                                </button>
                            </div>
                        }
                    >
                        {!loading && (
                            <Page
                                key={`page_${pageNumber}`}
                                pageNumber={pageNumber}
                                onLoadSuccess={onPageLoadSuccess}
                                rotate={(baseRotation + userRotation) % 360}
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                                width={Math.min(800, window.innerWidth - 100) * zoomLevel}
                                className="shadow-lg border border-gray-200"
                            />
                        )}
                    </Document>
                </div>
            </div>
        </div>
    );
};

export const FundrequestLightbox = ({
    isOpen,
    onClose,
    uploads,
    slideIndex,
}: FundrequestLightboxProps) => {
    // Early return if not open or no uploads
    if (!isOpen || !uploads || uploads.length === 0 || slideIndex === null) {
        return null;
    }

    // Ensure slideIndex is within bounds
    const safeSlideIndex = Math.max(0, Math.min(slideIndex, uploads.length - 1));

    // Check if the current file is a PDF
    const currentUpload = uploads[safeSlideIndex];
    const isPdf = isPdfFile(currentUpload?.filename);

    // If it's a PDF, show the PDF viewer
    if (isPdf) {
        return (
            <PDFViewer
                url={currentUpload.image_url}
                onClose={onClose}
                filename={currentUpload.filename}
            />
        );
    }

    // For non-PDF files, use the existing lightbox
    const images = uploads
        .filter(upload => !isPdfFile(upload.filename))
        .map((upload) => upload.image_url);

    // If no images to show (all PDFs), don't render the lightbox
    if (images.length === 0) {
        return null;
    }

    // PDFs are dropped from the slides, so the clicked upload's slide position
    // is the number of images that come before it, not its index in `uploads`.
    const adjustedSlideIndex = uploads
        .slice(0, safeSlideIndex)
        .filter(upload => !isPdfFile(upload.filename)).length;

    return (
        <Lightbox
            open={isOpen}
            close={onClose}
            slides={images.map((image) => ({ src: image }))}
            index={adjustedSlideIndex}
            plugins={[Download]}
        />
    );
};
